import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Check, Clock, Loader2, MessageSquare, RefreshCcw, ShieldCheck, GraduationCap, Download, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl, fmtINR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MKT_CSS, MktHeader, Stars, URL_KIND, useChatGate } from "./MktShared";
import { LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TIERS = ["basic", "standard", "premium"];

export default function MarketplaceItem() {
  const { kind, id } = useParams();
  const k = URL_KIND[kind];
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tier, setTier] = useState("basic");
  const [reqs, setReqs] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [payLink, setPayLink] = useState("");
  const [ref, setRef] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [openChat, gateEl] = useChatGate();
  const [wished, setWished] = useState(false);

  useEffect(() => {
    if (!k) { setNotFound(true); return; }
    api.get(`/mkt/${kind}/${id}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [kind, id, k]);

  const price = !item ? 0 : k === "service"
    ? (item.packages?.[tier]?.price ?? item.packages?.basic?.price ?? 0)
    : (item.offer_price || item.price || 0);

  const buy = async () => {
    if (!user) { toast.info("Login to purchase this listing"); navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mkt/orders", { kind: k, item_id: id, package: tier, requirements: reqs, aff: new URLSearchParams(window.location.search).get("aff") || "" });
      setOrder(data.order); setPayLink(data.payment_link || "");
      toast.success("Order created — complete payment to start");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitPayment = async () => {
    if (!ref.trim()) { toast.error("Enter your payment reference / UPI transaction ID"); return; }
    setBusy(true);
    try {
      await api.post(`/mkt/orders/${order.id}/submit-payment`, { reference: ref.trim() });
      setSubmitted(true);
      toast.success("Payment submitted for verification");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader />
      <div className="max-w-xl mx-auto text-center py-24 px-6" data-testid="mkt-item-404">
        <p className="font-display font-extrabold text-2xl">Listing not found</p>
        <p className="text-sm text-muted-foreground mt-2">It may have been removed or paused by the seller.</p>
        <Link to="/marketplace" className="inline-block mt-6 text-sm font-bold text-gradient">← Back to Marketplace</Link>
      </div>
    </div>);

  if (!item) return (
    <div className="min-h-screen bg-background"><style>{MKT_CSS}</style><MktHeader />
      <div className="max-w-7xl mx-auto px-6 py-12"><LoadingRows rows={4} /></div>
    </div>);

  const f = item.freelancer || {};
  const images = item.images?.length ? item.images : item.thumbnail ? [item.thumbnail] : [];
  const lessons = (item.modules || []).reduce((n, m) => n + (m.lessons?.length || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      {gateEl}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/marketplace" data-testid="mkt-item-back" className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Marketplace
        </Link>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          {/* LEFT */}
          <div className="space-y-8 min-w-0">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              <span className="mkt-cat-chip !bg-primary/90">{item.category || "General"}</span>
              <h1 data-testid="mkt-item-title" className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-3 leading-tight">{item.title}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Stars rating={item.rating} /> {item.rating || 0} ({item.reviews || 0} reviews)</span>
                <span>{item.sales || 0} sold</span>
                {k === "course" && <span className="flex items-center gap-1"><GraduationCap className="h-4 w-4" />{lessons} lessons · {item.level}</span>}
              </div>
            </motion.div>

            {images.length > 0 && (
              <div className="mkt-card !rounded-2xl">
                <img src={fileUrl(images[0])} alt={item.title} className="w-full max-h-[420px] object-cover" />
                {images.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto">
                    {images.slice(1).map((im, i) => <img key={i} src={fileUrl(im)} alt="" className="h-16 w-24 object-cover rounded-lg border border-border" />)}
                  </div>
                )}
              </div>
            )}

            {/* seller card */}
            <div className="mkt-card p-5 flex flex-wrap items-center gap-4" data-testid="mkt-item-seller">
              {f.photo
                ? <img src={fileUrl(f.photo)} alt={f.display_name} className="h-14 w-14 rounded-full object-cover ring-4 ring-primary/10" />
                : <div className="h-14 w-14 rounded-full mkt-grad-fallback flex items-center justify-center font-display font-extrabold text-white">{(f.display_name || "F")[0]}</div>}
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold flex items-center gap-1.5">{f.display_name} {f.verified && <BadgeCheck className="h-4 w-4 text-sky-500" />}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{f.title}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground"><Stars rating={f.rating} className="h-3 w-3" />({f.reviews || 0}) · {f.completed_orders || 0} orders completed</div>
              </div>
              <div className="flex gap-2">
                {f.slug && <Button variant="outline" size="sm" data-testid="mkt-item-view-seller" onClick={() => navigate(`/marketplace/f/${f.slug}`)} className="rounded-full font-bold">View Profile</Button>}
                {f.user_id && <Button size="sm" data-testid="mkt-item-contact" onClick={() => openChat(f.user_id, `chat about “${item.title}”`)}
                  className="rounded-full font-bold gradient-bg text-white border-0"><MessageSquare className="h-4 w-4 mr-1.5" />Chat with Freelancer</Button>}
              </div>
            </div>

            {/* description */}
            <div className="mkt-card p-6">
              <h2 className="font-display font-bold text-lg mb-3">About this {k}</h2>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed" data-testid="mkt-item-description">{item.description}</p>
              {item.license && <p className="text-xs mt-4 px-3 py-2 rounded-lg bg-muted text-muted-foreground"><b>License:</b> {item.license}</p>}
              {item.requirements && <p className="text-xs mt-2 px-3 py-2 rounded-lg bg-muted text-muted-foreground"><b>Buyer requirements:</b> {item.requirements}</p>}
            </div>

            {/* course curriculum preview */}
            {k === "course" && (item.modules || []).length > 0 && (
              <div className="mkt-card p-6" data-testid="mkt-course-curriculum">
                <h2 className="font-display font-bold text-lg mb-4">Course Curriculum</h2>
                <div className="space-y-4">
                  {item.modules.map((m, mi) => (
                    <div key={mi}>
                      <p className="font-bold text-sm">Module {mi + 1}: {m.title}</p>
                      <ul className="mt-2 space-y-1.5">
                        {(m.lessons || []).map((l, li) => (
                          <li key={li} className="text-xs text-muted-foreground flex items-center gap-2 pl-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{l.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            {(item.faq || []).length > 0 && (
              <div className="mkt-card p-6">
                <h2 className="font-display font-bold text-lg mb-4">FAQ</h2>
                <div className="space-y-3">
                  {item.faq.map((fqa, i) => (
                    <div key={i} className="rounded-xl border border-border p-4">
                      <p className="font-bold text-sm">{fqa.q}</p>
                      <p className="text-xs text-muted-foreground mt-1">{fqa.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — purchase panel */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mkt-card p-6 space-y-5 lg:sticky lg:top-24" data-testid="mkt-buy-panel">
            {!order ? (
              <>
                {k === "service" && (
                  <div className="grid grid-cols-3 gap-1.5 p-1 rounded-full bg-muted">
                    {TIERS.filter((t) => item.packages?.[t]).map((t) => (
                      <button key={t} data-testid={`mkt-tier-${t}`} onClick={() => setTier(t)}
                        className={`py-2 rounded-full text-xs font-bold capitalize transition-colors ${tier === t ? "gradient-bg text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
                    ))}
                  </div>
                )}
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground font-semibold">Price</span>
                  <span className="flex items-baseline gap-2">
                    {(item.offer_price || item.mrp) && k !== "service" && item.offer_price ? <span className="text-sm line-through text-muted-foreground">{fmtINR(item.price)}</span> : null}
                    <span data-testid="mkt-item-price" className="font-display font-extrabold text-3xl mkt-sec-title">{fmtINR(price)}</span>
                  </span>
                </div>
                {k === "service" && item.packages?.[tier] && (
                  <div className="space-y-2 text-sm">
                    {item.packages[tier].desc && <p className="text-xs text-muted-foreground">{item.packages[tier].desc}</p>}
                    <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {item.packages[tier].delivery_days} day delivery</p>
                    <p className="flex items-center gap-2"><RefreshCcw className="h-4 w-4 text-primary" /> {item.packages[tier].revisions} revision{item.packages[tier].revisions === 1 ? "" : "s"}</p>
                  </div>
                )}
                {k === "product" && <p className="flex items-center gap-2 text-sm"><Download className="h-4 w-4 text-primary" /> Instant download after payment verification</p>}
                {k === "course" && <p className="flex items-center gap-2 text-sm"><GraduationCap className="h-4 w-4 text-primary" /> Lifetime access · {lessons} lessons · {item.language}</p>}
                <div className="space-y-1.5">
                  <Label className="text-xs">Requirements for the seller (optional)</Label>
                  <Textarea data-testid="mkt-buy-requirements" value={reqs} onChange={(e) => setReqs(e.target.value)} rows={3} placeholder="Share links, brand details, goals…" />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="mkt-buy-now" onClick={buy} disabled={busy}
                    className="flex-1 h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm active:scale-95 transition-transform text-sm">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue · {fmtINR(price)}</>}
                  </Button>
                  <Button variant="outline" data-testid="mkt-wishlist-btn" disabled={busy}
                    onClick={async () => {
                      if (!user) { toast.info("Login to save to wishlist"); return; }
                      try { await api.post("/mkt/wishlist", { kind: k, item_id: id }); setWished(true); toast.success("Saved to wishlist"); }
                      catch (e) { toast.error(errMsg(e)); }
                    }}
                    className={`h-12 w-12 rounded-full shrink-0 ${wished ? "text-red-500 border-red-500/40" : ""}`}>
                    <Heart className={`h-5 w-5 ${wished ? "fill-red-500" : ""}`} />
                  </Button>
                </div>
                <p className="flex items-start gap-2 text-[11px] text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" /> Buyer protection: your payment is verified by MILTROO and released to the seller only after delivery approval.</p>
              </>
            ) : submitted ? (
              <div className="text-center space-y-3 py-4" data-testid="mkt-payment-submitted">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="h-7 w-7 text-emerald-500" /></div>
                <p className="font-display font-bold text-lg">Payment under verification</p>
                <p className="text-xs text-muted-foreground">MILTROO will verify your payment shortly. Track progress in My Orders.</p>
                <Button data-testid="mkt-goto-orders" onClick={() => navigate("/mkt/orders")} className="rounded-full font-bold gradient-bg text-white border-0">Go to My Orders</Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="mkt-payment-panel">
                <p className="font-display font-bold text-lg">Complete Payment</p>
                <p className="text-xs text-muted-foreground">Order <b>#{order.id.slice(0, 8)}</b> · {fmtINR(order.amount)}</p>
                {payLink
                  ? <a href={payLink} target="_blank" rel="noreferrer" data-testid="mkt-payment-link"
                      className="block text-center w-full h-11 leading-[2.75rem] rounded-full font-bold text-white text-sm gradient-bg glow-sm">Pay {fmtINR(order.amount)} Now ↗</a>
                  : <p className="text-xs px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">Payment link is not configured yet — please contact support to complete payment.</p>}
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment reference / UPI transaction ID</Label>
                  <Input data-testid="mkt-payment-reference" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 412345678901" />
                </div>
                <Button data-testid="mkt-payment-submit" onClick={submitPayment} disabled={busy} variant="outline" className="w-full rounded-full font-bold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I Have Paid — Submit Reference"}
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
